import React, { useState, useEffect, useRef } from 'react';
import { 
  Target, Plus, Upload, Search, Filter, MoreVertical, Mail, Phone, 
  MapPin, Building2, Calendar, User, MessageSquare, ChevronDown, 
  FileSpreadsheet, Download, Trash2, Edit2, UserPlus, Clock,
  CheckCircle, XCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { useAdminAuth } from '../context/AdminAuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/utils';

const BULK_TARGET_MEMBER_LIMIT = 50;
const TARGET_OUTREACH_TEMPLATE_TYPE = 'target_outreach';
const PROD_REGISTER_URL = 'https://oneglobealliance.com/register';

const normalizeTemplateField = (value) =>
  String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');

const getTargetTemplateDefaultValue = (target, fieldName) => {
  const normalized = normalizeTemplateField(fieldName);
  switch (normalized) {
    case 'first_name':
      return String(target?.contact_name || '').trim().split(/\s+/)[0] || '';
    case 'company_name':
      return target?.company_name || '';
    case 'affiliate_link':
      return PROD_REGISTER_URL;
    case 'register_link':
      return PROD_REGISTER_URL;
    case 'deck_link':
      return `${window.location.origin}/deck`;
    case 'demo_link':
      return PROD_REGISTER_URL;
    default:
      return '';
  }
};

const TargetMembers = () => {
  const [targets, setTargets] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');
  
  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [showFollowUpDialog, setShowFollowUpDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showSendEmailDialog, setShowSendEmailDialog] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null);
  
  const [bulkUploadData, setBulkUploadData] = useState({
    file: null,
    preview: [],
    errors: [],
    validating: false
  });
  const fileInputRef = useRef(null);
  const { api, admin, hasPermission } = useAdminAuth();
  const isAffiliate = admin?.role === 'affiliated_member';
  const canViewTargets = hasPermission('target_members', 'view');
  const canCreateTargets = hasPermission('target_members', 'create');
  const canEditTargets = hasPermission('target_members', 'edit');
  const canDeleteTargets = hasPermission('target_members', 'delete');
  const canAssignTargets = hasPermission('target_members', 'assign');
  const canAddFollowUps = hasPermission('target_members', 'follow_up');
  const canBulkImportTargets = hasPermission('target_members', 'bulk_import');

  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    source: '',
    notes: '',
    priority: 'medium'
  });

  const [followUpData, setFollowUpData] = useState({
    note: '',
    next_follow_up: '',
    useTemplate: false,
    selectedTemplate: null,
    emailSubject: '',
    emailBody: ''
  });
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [sendEmailData, setSendEmailData] = useState({
    templateId: '',
    templateVariables: {},
  });

  useEffect(() => {
    fetchTargets();
    if (canAssignTargets) {
      fetchTeamMembers();
    }
  }, [search, statusFilter, priorityFilter, assignedFilter]);

  const fetchTargets = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (priorityFilter) params.append('priority', priorityFilter);
      if (assignedFilter) params.append('assigned_to', assignedFilter);
      
      const response = await api.get(`/api/admin/target-members?${params}`);
      setTargets(response.data.members || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load target members');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await api.get('/api/admin/users?type=admin');
      const assignableUsers = (response.data.users || [])
        .filter((user) => user.status === 'active')
        .sort((a, b) => {
          const roleOrder = {
            affiliated_member: 0,
            finance_admin: 1,
            admin: 2,
            super_admin: 3,
          };
          const aRank = roleOrder[a.role] ?? 99;
          const bRank = roleOrder[b.role] ?? 99;
          if (aRank !== bRank) return aRank - bRank;
          return (a.full_name || '').localeCompare(b.full_name || '');
        });
      setTeamMembers(assignableUsers);
    } catch (error) {
      console.error('Failed to load team members');
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await api.get('/api/admin/team/templates');
      setEmailTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to load email templates');
    }
  };

  const handleAddTarget = async () => {
    try {
      await api.post('/api/admin/target-members', formData);
      toast.success('Target member added');
      setShowAddDialog(false);
      setFormData({
        company_name: '',
        contact_name: '',
        email: '',
        phone: '',
        country: '',
        city: '',
        source: '',
        notes: '',
        priority: 'medium'
      });
      fetchTargets();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to add target'));
    }
  };

  const handleUpdateStatus = async (targetId, newStatus) => {
    try {
      await api.put(`/api/admin/target-members/${targetId}`, { status: newStatus });
      toast.success('Status updated');
      fetchTargets();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAssign = async (adminId) => {
    try {
      await api.post(`/api/admin/target-members/${selectedTarget.target_id}/assign?assigned_to=${adminId}`);
      toast.success('Target assigned');
      setShowAssignDialog(false);
      fetchTargets();
    } catch (error) {
      toast.error('Failed to assign target');
    }
  };

  const handleAddFollowUp = async () => {
    try {
      await api.post(
        `/api/admin/target-members/${selectedTarget.target_id}/follow-up?note=${encodeURIComponent(followUpData.note)}${followUpData.next_follow_up ? `&next_follow_up=${followUpData.next_follow_up}` : ''}`
      );
      toast.success('Follow-up added');
      setShowFollowUpDialog(false);
      setFollowUpData({ note: '', next_follow_up: '', useTemplate: false, selectedTemplate: null, emailSubject: '', emailBody: '' });
      fetchTargets();
    } catch (error) {
      toast.error('Failed to add follow-up');
    }
  };

  const handleDelete = async (targetId) => {
    if (!window.confirm('Are you sure you want to delete this target?')) return;
    try {
      await api.delete(`/api/admin/target-members/${targetId}`);
      toast.success('Target deleted');
      fetchTargets();
    } catch (error) {
      toast.error('Failed to delete target');
    }
  };

  const openSendEmailDialog = async (target) => {
    setSelectedTarget(target);
    setSendEmailData({ templateId: '', templateVariables: {} });
    setShowSendEmailDialog(true);
    if (emailTemplates.length === 0) {
      await fetchEmailTemplates();
    }
  };

  const handleTemplateSelection = (templateId) => {
    const template = emailTemplates.find((item) => item.template_id === templateId);
    const templateVariables = {};
    (template?.variables || []).forEach((fieldName) => {
      templateVariables[fieldName] = getTargetTemplateDefaultValue(selectedTarget, fieldName);
    });
    setSendEmailData({ templateId, templateVariables });
  };

  const handleSendTargetEmail = async () => {
    if (!selectedTarget || !sendEmailData.templateId) {
      toast.error('Select a template first');
      return;
    }

    const template = emailTemplates.find((item) => item.template_id === sendEmailData.templateId);
    const missingFields = (template?.variables || []).filter(
      (fieldName) => !String(sendEmailData.templateVariables[fieldName] || '').trim()
    );
    if (missingFields.length > 0) {
      toast.error(`Fill these fields: ${missingFields.join(', ')}`);
      return;
    }

    try {
      const response = await api.post(`/api/admin/target-members/${selectedTarget.target_id}/send-email`, {
        template_id: sendEmailData.templateId,
        template_variables: sendEmailData.templateVariables,
      });
      toast.success(response.data?.message || 'Email sent');
      setShowSendEmailDialog(false);
      setSendEmailData({ templateId: '', templateVariables: {} });
      fetchTargets();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to send email'));
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['text/csv', 'application/vnd.ms-excel'];
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }

    setBulkUploadData({ ...bulkUploadData, file, validating: true, errors: [] });

    try {
      const text = await file.text();
      const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error('File appears to be empty or invalid');
        setBulkUploadData({ ...bulkUploadData, validating: false });
        return;
      }

      const dataRowCount = lines.length - 1;
      if (dataRowCount > BULK_TARGET_MEMBER_LIMIT) {
        toast.error(`You can upload up to ${BULK_TARGET_MEMBER_LIMIT} rows at a time`);
        setBulkUploadData({ ...bulkUploadData, validating: false });
        return;
      }

      // Parse CSV line properly handling quoted fields
      const parseCSVLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          
          if (char === '"') {
            if (inQuotes && line[i + 1] === '"') {
              current += '"';
              i++; // Skip next quote
            } else {
              inQuotes = !inQuotes;
            }
          } else if (char === ',' && !inQuotes) {
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        result.push(current.trim());
        return result;
      };

      // Parse headers
      const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase());
      const requiredFields = ['company_name', 'contact_name', 'email', 'country'];
      const missingFields = requiredFields.filter(f => !headers.includes(f));
      
      if (missingFields.length > 0) {
        toast.error(`Missing required columns: ${missingFields.join(', ')}`);
        setBulkUploadData({ ...bulkUploadData, validating: false });
        return;
      }

      // Parse and validate data
      const preview = [];
      const errors = [];
      
      for (let i = 1; i < Math.min(lines.length, BULK_TARGET_MEMBER_LIMIT + 1); i++) {
        const values = parseCSVLine(lines[i]);
        const row = {};
        headers.forEach((h, idx) => {
          row[h] = values[idx] || '';
        });

        // Validate each row
        const rowErrors = [];
        if (!row.company_name) rowErrors.push('Company name is required');
        if (!row.contact_name) rowErrors.push('Contact name is required');
        if (!row.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) rowErrors.push('Valid email is required');
        if (!row.country) rowErrors.push('Country is required');
        if (row.phone && !/^[\+]?[\d\s\-\(\)]{7,20}$/.test(row.phone)) rowErrors.push('Invalid phone format');
        if (row.priority && !['high', 'medium', 'low'].includes(row.priority.toLowerCase())) {
          rowErrors.push('Priority must be high, medium, or low');
        }
        if (row.assign_to_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.assign_to_email)) {
          rowErrors.push('Assign to email must be a valid email address');
        }

        if (rowErrors.length > 0) {
          errors.push({ row: i, errors: rowErrors });
        }
        
        preview.push({ ...row, rowNum: i, isValid: rowErrors.length === 0 });
      }

      setBulkUploadData({
        file,
        preview,
        errors,
        validating: false
      });
      
      if (errors.length > 0) {
        toast.warning(`${errors.length} rows have validation errors`);
      } else {
        toast.success(`Parsed ${preview.length} rows successfully`);
      }
    } catch (error) {
      toast.error('Failed to parse file');
      setBulkUploadData({ ...bulkUploadData, validating: false });
    }
  };

  const handleBulkSubmit = async () => {
    if (bulkUploadData.errors.length > 0) {
      toast.error('Please fix validation errors before submitting');
      return;
    }

    try {
      const members = bulkUploadData.preview.map(row => ({
        company_name: row.company_name,
        contact_name: row.contact_name,
        email: row.email,
        phone: row.phone || null,
        country: row.country,
        city: row.city || null,
        source: row.source || null,
        notes: row.notes || null,
        priority: (row.priority || 'medium').toLowerCase(),
        ...(row.assign_to_email?.trim() && { assign_to_email: row.assign_to_email.trim() })
      }));

      const res = await api.post('/api/admin/target-members/bulk', { members });
      const { created = 0, failed: failedCount = 0, failed_list = [] } = res.data || {};
      if (failedCount > 0 && failed_list?.length > 0) {
        const msg = failed_list.map(f => `${f.email}: ${f.reason}`).join('; ');
        toast.error(`${failedCount} row(s) failed: ${msg}`);
      } else {
        toast.success(`Bulk upload completed: ${created} target(s) created`);
      }
      setShowBulkDialog(false);
      setBulkUploadData({ file: null, preview: [], errors: [], validating: false });
      fetchTargets();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Bulk upload failed'));
    }
  };

  const downloadTemplate = () => {
    // CSV headers matching the backend requirements (assign_to_email = team member email to assign target to)
    const headers = ['company_name', 'contact_name', 'email', 'phone', 'country', 'city', 'source', 'notes', 'priority', 'assign_to_email'];
    
    // Sample data rows showing different scenarios
    const rows = [
      // Full data example with assign_to_email
      'ABC Logistics,John Smith,john@abclogistics.com,+1-555-123-4567,United States,New York,Website,Interested in Platinum tier,high,sales@oneglobe.com',
      // Minimal required fields example (no assign)
      '"XYZ Freight Ltd",Jane Doe,jane@xyzfreight.com,,United Kingdom,London,Referral,,medium,',
      // Company with comma in name (quoted)
      '"Smith, Johnson & Co",Robert Brown,robert@sjco.com,+44-20-1234-5678,United Kingdom,Manchester,Event,Follow up in 2 weeks,low,sales@oneglobe.com'
    ];
    
    const csvContent = headers.join(',') + '\n' + rows.join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'target_members_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Template downloaded. Required: company_name, contact_name, email, country. Optional: assign_to_email (team member email)');
  };

  const getAffiliateStageBadge = (stage) => {
    const normalizedStage = stage || 'Referred';
    const badges = {
      Referred: { bg: 'bg-gray-100', text: 'text-gray-700' },
      'Registered Interest': { bg: 'bg-sky-100', text: 'text-sky-700' },
      'Docs Submitted': { bg: 'bg-amber-100', text: 'text-amber-700' },
      'Verification Complete': { bg: 'bg-indigo-100', text: 'text-indigo-700' },
      Successful: { bg: 'bg-emerald-100', text: 'text-emerald-700' }
    };
    return badges[normalizedStage] || badges.Referred;
  };

  const formatDateTime = (dateString, includeTime = true) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '-';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = includeTime ? String(date.getFullYear()).slice(-2) : date.getFullYear();
      if (!includeTime) return `${day}/${month}/${year}`;
      const hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      return `${day}/${month}/${year} ${displayHours}:${minutes} ${ampm}`;
    } catch (e) {
      return '-';
    }
  };

  const renderAffiliateStage = (target) => {
    const stage = target.affiliate_stage
      || (['interested', 'registered'].includes(String(target.status || '').toLowerCase()) ? 'Registered Interest' : null);
    if (!stage) {
      return (
        <div className="space-y-1.5">
          <Badge className="bg-gray-100 text-gray-700 text-[10px] px-2 py-0.5">
            {target.status || 'New'}
          </Badge>
          <div className="text-[10px] text-gray-500 leading-4">
            Target prospect
          </div>
        </div>
      );
    }
    const stageBadge = getAffiliateStageBadge(stage);
    return (
      <div className="space-y-1.5">
        <Badge className={`${stageBadge.bg} ${stageBadge.text} text-[10px] px-2 py-0.5`}>
          {stage}
        </Badge>
        {stage === 'Successful' && target.successful_at && (
          <div className="text-[10px] text-emerald-700 leading-4">
            Successful on {formatDateTime(target.successful_at, false)}
          </div>
        )}
        {target.status && (
          <div className="text-[10px] text-gray-500 leading-4">
            Target: {target.status}
          </div>
        )}
      </div>
    );
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      high: { bg: 'bg-red-100', text: 'text-red-700' },
      medium: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
      low: { bg: 'bg-gray-100', text: 'text-gray-700' }
    };
    return badges[priority] || badges.medium;
  };

  const outreachTemplates = emailTemplates.filter(
    (template) => template.template_type === TARGET_OUTREACH_TEMPLATE_TYPE
  );
  const selectedEmailTemplate = outreachTemplates.find(
    (template) => template.template_id === sendEmailData.templateId
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Target Members</h1>
          <p className="text-gray-500">Manage prospects and potential members</p>
        </div>
        <div className="flex space-x-2">
          {canBulkImportTargets && (
            <Button variant="outline" onClick={() => setShowBulkDialog(true)}>
              <Upload size={16} className="mr-2" />
              Bulk Import
            </Button>
          )}
          {canCreateTargets && (
            <Button onClick={() => setShowAddDialog(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
              <Plus size={16} className="mr-2" />
              {isAffiliate ? 'Refer Prospect' : 'Add Target'}
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className={`grid gap-4 ${canAssignTargets ? 'grid-cols-5' : 'grid-cols-4'}`}>
            <div className="col-span-2">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search by company, name, or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 text-sm h-9"
                />
              </div>
            </div>
            <select
              className="px-3 py-1.5 border rounded-md text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="interested">Interested</option>
              <option value="registered">Registered</option>
              <option value="declined">Declined</option>
            </select>
            <select
              className="px-3 py-1.5 border rounded-md text-sm"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <option value="">All Priority</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            {canAssignTargets && (
              <select
                className="px-3 py-1.5 border rounded-md text-sm"
                value={assignedFilter}
                onChange={(e) => setAssignedFilter(e.target.value)}
              >
                <option value="">All Assigned</option>
                {teamMembers.map(tm => (
                  <option key={tm.admin_id} value={tm.admin_id}>{tm.full_name}</option>
                ))}
              </select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">{total} target members found</p>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-hidden">
          <table className="w-full table-fixed text-xs border-separate border-spacing-x-2">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-4 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wide w-[18%] whitespace-nowrap">Company / Contact</th>
                <th className="px-4 py-4 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wide w-[6%] whitespace-nowrap">Contact</th>
                <th className="px-4 py-4 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wide w-[7%] whitespace-nowrap">Source</th>
                <th className="px-4 py-4 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wide w-[10%] whitespace-nowrap">Location</th>
                <th className="px-4 py-4 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wide w-[12%] whitespace-nowrap">Member Status</th>
                <th className="px-4 py-4 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wide w-[13%] whitespace-nowrap">Next Step Pending</th>
                <th className="px-4 py-4 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wide w-[7%] whitespace-nowrap">Priority</th>
                <th className="px-4 py-4 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wide w-[10%] whitespace-nowrap">Assign To</th>
                <th className="px-4 py-4 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wide w-[10%] whitespace-nowrap min-w-[7rem]">Next Follow Up</th>
                <th className="px-4 py-4 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wide w-[7%] whitespace-nowrap min-w-[5rem]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="10" className="px-5 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : targets.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-5 py-8 text-center text-gray-500">No target members found</td>
                </tr>
              ) : (
                targets.map((target) => {
                  const priorityBadge = getPriorityBadge(target.priority);
                  
                  // Get the next_follow_up date - prefer top-level field, fallback to most recent from follow_ups array
                  const getNextFollowUpDate = () => {
                    // First check if there's a top-level next_follow_up field
                    if (target.next_follow_up) {
                      return target.next_follow_up;
                    }
                    // Fallback: find the most recent next_follow_up from follow_ups array
                    if (target.follow_ups && target.follow_ups.length > 0) {
                      const followUpsWithDate = target.follow_ups
                        .filter(fu => fu.next_follow_up)
                        .sort((a, b) => {
                          const dateA = new Date(a.next_follow_up);
                          const dateB = new Date(b.next_follow_up);
                          return dateB - dateA; // Most recent first
                        });
                      return followUpsWithDate.length > 0 ? followUpsWithDate[0].next_follow_up : null;
                    }
                    return null;
                  };
                  
                  const nextFollowUpDate = getNextFollowUpDate();
                  
                  return (
                    <tr 
                      key={target.target_id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={(e) => {
                        // Don't trigger if clicking on action buttons or dropdown
                        if (e.target.closest('button') || e.target.closest('[role="menu"]')) {
                          return;
                        }
                        setSelectedTarget(target);
                        setShowDetailsDialog(true);
                      }}
                    >
                      <td className="px-4 py-4 align-top">
                        <div className="break-words">
                          <p className="font-medium">{target.company_name} – {target.contact_name}</p>
                          <p className="text-[10px] text-gray-500">{target.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-3">
                          {target.phone && (
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`tel:${target.phone}`, '_self');
                              }}
                              className="p-1.5 hover:bg-green-100 rounded"
                              title="Call"
                            >
                              <Phone size={14} className="text-green-600" />
                            </button>
                          )}
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`mailto:${target.email}`, '_blank');
                            }}
                            className="p-1.5 hover:bg-blue-100 rounded"
                            title="Email"
                          >
                            <Mail size={14} className="text-blue-600" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <Badge variant="outline" className="text-[10px] px-2 py-0.5">
                          {target.source || 'Direct'}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="flex items-center text-gray-600 break-words gap-2">
                          <MapPin size={12} className="shrink-0" />
                          <span>{target.city ? `${target.city}, ` : ''}{target.country}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        {renderAffiliateStage(target)}
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="text-[11px] leading-4 text-gray-700 break-words">
                          {target.next_step_pending || 'Company documents submission pending'}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <Badge className={`${priorityBadge.bg} ${priorityBadge.text} text-[10px] px-2 py-0.5`}>
                          {target.priority}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 align-top" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <span className="break-words text-gray-600">
                            {target.assigned_to 
                              ? (target.assigned_to === admin?.admin_id
                                  ? 'Assigned to me'
                                  : (teamMembers.find(tm => tm.admin_id === target.assigned_to)?.full_name || target.assigned_to))
                              : 'Unassigned'}
                          </span>
                          {canAssignTargets && (
                            <button 
                              onClick={() => { setSelectedTarget(target); setShowAssignDialog(true); }}
                              className="p-1 hover:bg-gray-200 rounded shrink-0"
                              title={target.assigned_to ? 'Reassign' : 'Assign'}
                            >
                              <ChevronDown size={14} className="text-gray-500" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="text-gray-600 whitespace-nowrap">
                          {nextFollowUpDate ? (
                            <span>{formatDateTime(nextFollowUpDate)}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right align-top" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                              <MoreVertical size={14} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canAssignTargets && (
                              <DropdownMenuItem onClick={() => { setSelectedTarget(target); setShowAssignDialog(true); }}>
                                <UserPlus size={14} className="mr-2" /> Assign
                              </DropdownMenuItem>
                            )}
                            {canAddFollowUps && (
                              <DropdownMenuItem onClick={() => { setSelectedTarget(target); setShowFollowUpDialog(true); }}>
                                <MessageSquare size={14} className="mr-2" /> Add Follow-up
                              </DropdownMenuItem>
                            )}
                            {canAddFollowUps && (
                              <DropdownMenuItem onClick={() => openSendEmailDialog(target)}>
                                <Mail size={14} className="mr-2" /> Send Email
                              </DropdownMenuItem>
                            )}
                            {canEditTargets && (
                              <DropdownMenuItem onClick={() => handleUpdateStatus(target.target_id, 'contacted')}>
                                <Phone size={14} className="mr-2" /> Mark Contacted
                              </DropdownMenuItem>
                            )}
                            {canEditTargets && (
                              <DropdownMenuItem onClick={() => handleUpdateStatus(target.target_id, 'interested')}>
                                <Target size={14} className="mr-2" /> Mark Interested
                              </DropdownMenuItem>
                            )}
                            {canDeleteTargets && (
                              <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(target.target_id)}>
                                <Trash2 size={14} className="mr-2" /> Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Add Target Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isAffiliate ? 'Refer Potential Member' : 'Add Target Member'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Company Name *</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Company name"
                />
              </div>
              <div>
                <Label>Contact Name *</Label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  placeholder="Contact person"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@company.com"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 234 567 890"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Country *</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="Country"
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="City"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Source</Label>
                <select
                  className="w-full px-3 py-2 border rounded-md"
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                >
                  <option value="">Select source</option>
                  <option value="affiliate_referral">Affiliate Referral</option>
                  <option value="website">Website</option>
                  <option value="referral">Referral</option>
                  <option value="event">Event</option>
                  <option value="cold_outreach">Cold Outreach</option>
                  <option value="linkedin">LinkedIn</option>
                </select>
              </div>
              <div>
                <Label>Priority</Label>
                <select
                  className="w-full px-3 py-2 border rounded-md"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                >
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button onClick={handleAddTarget} className="bg-blue-600 hover:bg-blue-700 text-white">
                {isAffiliate ? 'Submit Referral' : 'Add Target'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Import Targets</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!bulkUploadData.file ? (
              <>
                <p className="text-sm text-gray-600">
                  Upload a CSV file with target member data. Required columns: company_name, contact_name, email, country. Optional: assign_to_email (team member email to assign the target to)
                </p>
                <div className="p-4 border-2 border-dashed rounded-lg text-center">
                  <FileSpreadsheet size={40} className="mx-auto text-gray-400 mb-2" />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleCsvUpload}
                    className="hidden"
                  />
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                    <Upload size={16} className="mr-2" />
                    Select CSV File
                  </Button>
                </div>
                <Button variant="link" onClick={downloadTemplate} className="text-blue-600">
                  <Download size={14} className="mr-1" />
                  Download Template
                </Button>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <FileSpreadsheet size={20} className="text-green-600" />
                    <span className="font-medium">{bulkUploadData.file.name}</span>
                    <Badge className={bulkUploadData.errors.length === 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {bulkUploadData.errors.length === 0 ? 'Valid' : `${bulkUploadData.errors.length} errors`}
                    </Badge>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => {
                    setBulkUploadData({ file: null, preview: [], errors: [], validating: false });
                    fileInputRef.current.value = '';
                  }}>
                    Change File
                  </Button>
                </div>

                {bulkUploadData.errors.length > 0 && (
                  <div className="p-4 bg-red-50 rounded-lg">
                    <h4 className="font-medium text-red-800 mb-2">Validation Errors:</h4>
                    <ul className="text-sm text-red-700 space-y-1 max-h-32 overflow-y-auto">
                      {bulkUploadData.errors.map((err, idx) => (
                        <li key={idx}>Row {err.row}: {err.errors.join(', ')}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <h4 className="font-medium mb-2">Preview (Up to 50 rows):</h4>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-2 text-left">Row</th>
                        <th className="px-2 py-2 text-left">Company</th>
                        <th className="px-2 py-2 text-left">Contact</th>
                        <th className="px-2 py-2 text-left">Email</th>
                        <th className="px-2 py-2 text-left">Phone</th>
                        <th className="px-2 py-2 text-left">Country</th>
                        <th className="px-2 py-2 text-left">Assign To</th>
                        <th className="px-2 py-2 text-left">Priority</th>
                        <th className="px-2 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkUploadData.preview.map((row, idx) => (
                        <tr key={idx} className={row.isValid ? '' : 'bg-red-50'}>
                          <td className="px-2 py-2">{row.rowNum}</td>
                          <td className="px-2 py-2">{row.company_name}</td>
                          <td className="px-2 py-2">{row.contact_name}</td>
                          <td className="px-2 py-2">{row.email}</td>
                          <td className="px-2 py-2">{row.phone || '-'}</td>
                          <td className="px-2 py-2">{row.country}</td>
                          <td className="px-2 py-2">{row.assign_to_email || '-'}</td>
                          <td className="px-2 py-2">{row.priority || 'medium'}</td>
                          <td className="px-2 py-2">
                            {row.isValid ? (
                              <CheckCircle size={16} className="text-green-600" />
                            ) : (
                              <XCircle size={16} className="text-red-600" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end space-x-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => {
                    setShowBulkDialog(false);
                    setBulkUploadData({ file: null, preview: [], errors: [], validating: false });
                  }}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleBulkSubmit}
                    disabled={bulkUploadData.errors.length > 0}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Upload size={16} className="mr-2" />
                    Upload {bulkUploadData.preview.length} Targets
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Follow-up Dialog */}
      <Dialog open={showFollowUpDialog} onOpenChange={(open) => {
        setShowFollowUpDialog(open);
        if (open) fetchEmailTemplates();
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Follow-up for {selectedTarget?.company_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Template Selection */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <Label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={followUpData.useTemplate}
                  onChange={(e) => setFollowUpData({ ...followUpData, useTemplate: e.target.checked })}
                  className="rounded"
                />
                <span>Use Email Template</span>
              </Label>
              
              {followUpData.useTemplate && (
                <div className="mt-3 space-y-3">
                  <select
                    className="w-full px-3 py-2 border rounded-md"
                    value={followUpData.selectedTemplate?.template_id || ''}
                    onChange={(e) => {
                      const template = emailTemplates.find(t => t.template_id === e.target.value);
                      setFollowUpData({
                        ...followUpData,
                        selectedTemplate: template,
                        emailSubject: template?.subject || '',
                        emailBody: template?.body || ''
                      });
                    }}
                  >
                    <option value="">Select a template...</option>
                    {emailTemplates.filter(t => t.template_type === 'follow_up' || t.template_type === 'general').map(template => (
                      <option key={template.template_id} value={template.template_id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                  
                  {followUpData.selectedTemplate && (
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs">Subject</Label>
                        <Input
                          value={followUpData.emailSubject}
                          onChange={(e) => setFollowUpData({ ...followUpData, emailSubject: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Email Body</Label>
                        <Textarea
                          value={followUpData.emailBody}
                          onChange={(e) => setFollowUpData({ ...followUpData, emailBody: e.target.value })}
                          rows={4}
                          className="mt-1 text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <Label>Note *</Label>
              <Textarea
                value={followUpData.note}
                onChange={(e) => setFollowUpData({ ...followUpData, note: e.target.value })}
                placeholder="What was discussed or actioned..."
                rows={3}
              />
            </div>
            <div>
              <Label>Next Follow-up Date</Label>
              <Input
                type="datetime-local"
                value={followUpData.next_follow_up}
                onChange={(e) => setFollowUpData({ ...followUpData, next_follow_up: e.target.value })}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => {
                setShowFollowUpDialog(false);
                setFollowUpData({ note: '', next_follow_up: '', useTemplate: false, selectedTemplate: null, emailSubject: '', emailBody: '' });
              }}>Cancel</Button>
              <Button onClick={handleAddFollowUp} className="bg-blue-600 hover:bg-blue-700 text-white">
                Add Follow-up
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={showSendEmailDialog} onOpenChange={setShowSendEmailDialog}>
        <DialogContent className="flex h-[88vh] w-[calc(100vw-2rem)] max-w-2xl flex-col overflow-hidden p-0">
          <DialogHeader className="shrink-0 px-6 pb-0 pt-6">
            <DialogTitle>Send Email to {selectedTarget?.company_name}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-4 pt-2">
            <div className="space-y-4">
            <div>
              <Label>Email Template *</Label>
              <select
                className="w-full mt-1 px-3 py-2 border rounded-md"
                value={sendEmailData.templateId}
                onChange={(e) => handleTemplateSelection(e.target.value)}
              >
                <option value="">Select a template...</option>
                {outreachTemplates.map((template) => (
                  <option key={template.template_id} value={template.template_id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>

            {selectedEmailTemplate && (
              <>
                <div className="rounded-lg border bg-gray-50 p-4 max-h-64 overflow-y-auto">
                  <p className="text-sm font-medium text-gray-900">{selectedEmailTemplate.subject}</p>
                  <p className="mt-2 text-xs text-gray-500 whitespace-pre-line break-words">{selectedEmailTemplate.body}</p>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {(selectedEmailTemplate.variables || []).map((fieldName) => (
                    <div key={fieldName}>
                      <Label>{fieldName} *</Label>
                      <Input
                        value={sendEmailData.templateVariables[fieldName] || ''}
                        onChange={(e) =>
                          setSendEmailData((prev) => ({
                            ...prev,
                            templateVariables: {
                              ...prev.templateVariables,
                              [fieldName]: e.target.value,
                            },
                          }))
                        }
                        placeholder={`Enter ${fieldName}`}
                        className="mt-1"
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
            </div>
          </div>

            <div className="shrink-0 border-t bg-background px-6 py-4">
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSendEmailDialog(false);
                  setSendEmailData({ templateId: '', templateVariables: {} });
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSendTargetEmail} className="bg-blue-600 hover:bg-blue-700 text-white">
                Send
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Target to Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Assign <strong>{selectedTarget?.company_name}</strong> to:
            </p>
            <div className="space-y-2">
              {teamMembers.map(tm => (
                <button
                  key={tm.admin_id}
                  onClick={() => handleAssign(tm.admin_id)}
                  className="w-full p-3 border rounded-lg text-left hover:bg-gray-50 flex items-center space-x-3"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <User size={18} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">{tm.full_name}</p>
                    <p className="text-xs text-gray-500">{tm.email}</p>
                    <p className="text-xs text-blue-600 capitalize">
                      {(tm.role_name || tm.role || '').replace(/_/g, ' ')}
                    </p>
                  </div>
                </button>
              ))}
              {teamMembers.length === 0 && (
                <div className="rounded-lg border border-dashed p-4 text-sm text-gray-500">
                  No active admin users are available for assignment.
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Target Details Dialog */}
      {selectedTarget && (
        <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <Building2 size={20} className="mr-2 text-blue-600" />
                {selectedTarget.company_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Base Information */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Base Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 mb-1">Contact Person</p>
                    <p className="font-medium text-gray-900">{selectedTarget.contact_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Email</p>
                    <p className="font-medium text-gray-900">{selectedTarget.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Phone</p>
                    <p className="font-medium text-gray-900">{selectedTarget.phone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Location</p>
                    <p className="font-medium text-gray-900">
                      {selectedTarget.city ? `${selectedTarget.city}, ` : ''}{selectedTarget.country}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Source</p>
                    <p className="font-medium text-gray-900">{selectedTarget.source || 'Direct'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Member Status</p>
                    {renderAffiliateStage(selectedTarget)}
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Next Step Pending</p>
                    <p className="font-medium text-gray-900">
                      {selectedTarget.next_step_pending || 'Company documents submission pending'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Priority</p>
                    <Badge className={`${getPriorityBadge(selectedTarget.priority).bg} ${getPriorityBadge(selectedTarget.priority).text}`}>
                      {selectedTarget.priority}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Next Follow-up</p>
                    <p className="font-medium text-gray-900">
                      {(() => {
                        const getNextFollowUpDate = () => {
                          if (selectedTarget.next_follow_up) {
                            return selectedTarget.next_follow_up;
                          }
                          if (selectedTarget.follow_ups && selectedTarget.follow_ups.length > 0) {
                            const followUpsWithDate = selectedTarget.follow_ups
                              .filter(fu => fu.next_follow_up)
                              .sort((a, b) => {
                                const dateA = new Date(a.next_follow_up);
                                const dateB = new Date(b.next_follow_up);
                                return dateB - dateA;
                              });
                            return followUpsWithDate.length > 0 ? followUpsWithDate[0].next_follow_up : null;
                          }
                          return null;
                        };
                        const nextDate = getNextFollowUpDate();
                        if (!nextDate) return '-';
                        const date = new Date(nextDate);
                        const day = String(date.getDate()).padStart(2, '0');
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const year = date.getFullYear();
                        const hours = date.getHours();
                        const minutes = String(date.getMinutes()).padStart(2, '0');
                        const ampm = hours >= 12 ? 'PM' : 'AM';
                        const displayHours = hours % 12 || 12;
                        return `${day}/${month}/${year} ${displayHours}:${minutes} ${ampm}`;
                      })()}
                    </p>
                  </div>
                  {selectedTarget.affiliate_stage === 'Successful' && (
                    <div>
                      <p className="text-gray-500 mb-1">Successful Date</p>
                      <p className="font-medium text-emerald-700">
                        {formatDateTime(selectedTarget.successful_at, false)}
                      </p>
                    </div>
                  )}
                  {selectedTarget.notes && (
                    <div className="col-span-2">
                      <p className="text-gray-500 mb-1">Notes</p>
                      <p className="font-medium text-gray-900">{selectedTarget.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Follow-up Notes List */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <MessageSquare size={18} className="mr-2 text-blue-600" />
                  Follow-up Notes ({selectedTarget.follow_ups?.length || 0})
                </h3>
                {selectedTarget.follow_ups && selectedTarget.follow_ups.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {selectedTarget.follow_ups.map((note, idx) => (
                      <div key={idx} className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex items-start justify-between mb-2">
                          <p className="text-xs text-gray-500">
                            {note.added_at 
                              ? (() => {
                                  const date = new Date(note.added_at);
                                  const day = String(date.getDate()).padStart(2, '0');
                                  const month = String(date.getMonth() + 1).padStart(2, '0');
                                  const year = date.getFullYear();
                                  const hours = date.getHours();
                                  const minutes = String(date.getMinutes()).padStart(2, '0');
                                  const ampm = hours >= 12 ? 'PM' : 'AM';
                                  const displayHours = hours % 12 || 12;
                                  return `${day}/${month}/${year} ${displayHours}:${minutes} ${ampm}`;
                                })()
                              : 'Date not available'
                            }
                          </p>
                          {note.next_follow_up && (
                            <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">
                              Next: {(() => {
                                const date = new Date(note.next_follow_up);
                                const day = String(date.getDate()).padStart(2, '0');
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const year = date.getFullYear();
                                const hours = date.getHours();
                                const minutes = String(date.getMinutes()).padStart(2, '0');
                                const ampm = hours >= 12 ? 'PM' : 'AM';
                                const displayHours = hours % 12 || 12;
                                return `${day}/${month}/${year} ${displayHours}:${minutes} ${ampm}`;
                              })()}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-900">{note.note || 'No note provided'}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm py-4 text-center">No follow-up notes available</p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default TargetMembers;
